/**
 * Notification API service.
 * Provides methods for fetching and managing in-app notifications.
 */

import { API_BASE_URL } from '@/config/env'
import { apiRequest } from '@/services/api/interceptor'
import type { Notification, NotificationApiError } from './types'

const NOTIFICATIONS_API = `${API_BASE_URL}/api/core/notifications`

interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
  count?: number
}

function unwrap<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as ApiResponse<T>).data
  }
  return response as T
}

/**
 * Get all notifications for the current user.
 */
export async function getNotifications(): Promise<Notification[]> {
  try {
    const response = await apiRequest<ApiResponse<Notification[]> | Notification[]>(
      NOTIFICATIONS_API,
      { method: 'GET' }
    )
    return unwrap<Notification[]>(response)
  } catch (error) {
    console.error('[notificationApi] Error fetching notifications:', error)
    throw error as NotificationApiError
  }
}

/**
 * Get the unread notification count.
 */
export async function getUnreadCount(): Promise<number> {
  try {
    const response = await apiRequest<ApiResponse<{ unreadCount: number }> | { unreadCount: number }>(
      `${NOTIFICATIONS_API}/unread-count`,
      { method: 'GET' }
    )
    const data = unwrap<{ unreadCount: number }>(response)
    return data.unreadCount
  } catch (error) {
    console.error('[notificationApi] Error fetching unread count:', error)
    return 0
  }
}

/**
 * Mark a single notification as read.
 */
export async function markAsRead(notificationId: string): Promise<void> {
  try {
    await apiRequest<void>(
      `${NOTIFICATIONS_API}/${notificationId}/read`,
      { method: 'PUT' }
    )
  } catch (error) {
    console.error('[notificationApi] Error marking notification as read:', error)
    throw error as NotificationApiError
  }
}

/**
 * Mark all notifications as read.
 */
export async function markAllAsRead(): Promise<void> {
  try {
    await apiRequest<void>(
      `${NOTIFICATIONS_API}/read-all`,
      { method: 'PUT' }
    )
  } catch (error) {
    console.error('[notificationApi] Error marking all as read:', error)
    throw error as NotificationApiError
  }
}

/**
 * Delete a notification.
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  try {
    await apiRequest<void>(
      `${NOTIFICATIONS_API}/${notificationId}`,
      { method: 'DELETE' }
    )
  } catch (error) {
    console.error('[notificationApi] Error deleting notification:', error)
    throw error as NotificationApiError
  }
}

const notificationApi = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
}

export default notificationApi
